/**
 * Medications service — FHIR MedicationStatement-backed active med list.
 *
 * The list the provider sees as "what is this patient taking right now."
 * Populated from patient-reported meds, external pharmacy fills, CCDA
 * imports, and Rx the clinic itself writes (auto-upserted server-side
 * when a Prescription is verified).
 */

import { api } from './api';

export type MedicationStatus =
  | 'active' | 'completed' | 'entered-in-error' | 'intended' | 'stopped' | 'on-hold' | 'unknown';

export type MedicationSource =
  | 'patient_reported' | 'rx_this_clinic' | 'rx_external' | 'imported_ccda' | 'pharmacy_fill';

export interface PatientMedication {
  id: string;
  patient_id: string;
  clinic_id: string;
  encounter_id?: string | null;
  prescription_id?: string | null;
  medication_code_system: string;
  medication_code?: string | null;
  medication_display: string;
  dose_text?: string | null;
  route?: string | null;
  status: MedicationStatus;
  source: MedicationSource;
  effective_start?: string | null;
  effective_end?: string | null;
  last_reconciled_at?: string | null;
  last_reconciled_by_practitioner_id?: string | null;
  note?: string | null;
  created_at?: string;
  updated_at?: string;
}

export interface CreateMedicationPayload {
  medicationDisplay: string;
  medicationCode?: string;
  medicationCodeSystem?: string;
  doseText?: string;
  route?: string;
  status?: MedicationStatus;
  source?: MedicationSource;
  effectiveStart?: string;
  effectiveEnd?: string;
  note?: string;
  // Encounter linkage — required by requireClinicalContext middleware.
  encounterId?: string;
  off_encounter_reason?:
    | 'refill_request'
    | 'chart_correction'
    | 'letter'
    | 'external_import'
    | 'pre_visit_task'
    | 'direct_rx';
}

export interface MedicationReconciliationResult {
  id: string;
  patient_id: string;
  clinic_id: string;
  encounter_id?: string | null;
  practitioner_id: string;
  performed_at: string;
  medications_reviewed_count: number;
  medications_added_count: number;
  medications_stopped_count: number;
  medications_unchanged_count: number;
  no_active_medications: boolean;
  notes?: string | null;
}

export const medicationsService = {
  async getPatientMedications(
    clinicId: string,
    patientId: string,
    options?: { activeOnly?: boolean; status?: MedicationStatus },
  ): Promise<PatientMedication[]> {
    const params = new URLSearchParams();
    if (options?.activeOnly) params.append('activeOnly', 'true');
    if (options?.status) params.append('status', options.status);
    const qs = params.toString();
    const response = await api.get(
      `/emr/${clinicId}/patients/${patientId}/medications${qs ? `?${qs}` : ''}`,
    );
    return response.data || [];
  },

  async createMedication(
    clinicId: string,
    patientId: string,
    payload: CreateMedicationPayload,
  ): Promise<PatientMedication> {
    const body: CreateMedicationPayload = {
      ...payload,
      off_encounter_reason:
        payload.off_encounter_reason
        || (payload.encounterId ? undefined : 'chart_correction'),
    };
    const response = await api.post(
      `/emr/${clinicId}/patients/${patientId}/medications`,
      body,
    );
    return response.data || response;
  },

  async updateMedication(
    clinicId: string,
    medicationId: string,
    updates: Partial<{
      medicationDisplay: string;
      medicationCode: string;
      doseText: string;
      route: string;
      status: MedicationStatus;
      effectiveStart: string;
      effectiveEnd: string;
      note: string;
    }>,
  ): Promise<PatientMedication> {
    const response = await api.put(
      `/emr/${clinicId}/medications/${medicationId}`,
      updates,
    );
    return response.data || response;
  },

  async stopMedication(
    clinicId: string,
    medicationId: string,
    options?: { stoppedAt?: string; note?: string },
  ): Promise<PatientMedication> {
    const response = await api.post(
      `/emr/${clinicId}/medications/${medicationId}/stop`,
      options || {},
    );
    return response.data || response;
  },

  async recordReconciliation(
    clinicId: string,
    encounterId: string,
    payload: {
      reviewedCount?: number;
      addedCount?: number;
      stoppedCount?: number;
      unchangedCount?: number;
      noActiveMedications?: boolean;
      notes?: string;
    },
  ): Promise<MedicationReconciliationResult> {
    const response = await api.post(
      `/emr/${clinicId}/encounters/${encounterId}/medication-reconciliation`,
      payload,
    );
    return response.data || response;
  },

  async getLatestReconciliation(
    clinicId: string,
    encounterId: string,
  ): Promise<MedicationReconciliationResult | null> {
    const response = await api.get(
      `/emr/${clinicId}/encounters/${encounterId}/medication-reconciliation`,
    );
    return response.data ?? null;
  },
};

export default medicationsService;
